/**
 * Avatar Registry
 * Maps avatar IDs to mood-based image assets (loaded from R2).
 * Standard avatars are built from AVATAR_IDS; monkey is added separately (has unique `angry` mood).
 */

import { getAssetUrl, getSmallAssetUrl, ASSET_PATHS } from './assets';
import { AVATAR_IDS, type AvatarId } from './avatarIds';
import { MONKEY_AVATAR_ID } from './monkey';
import { AVATAR_MOODS } from '../src/avatars/types/avatar.types';
import type { AvatarRegistryType, AvatarRegistryEntry } from '../src/avatars/types/avatar.types';
import type { ImageSourcePropType } from 'react-native';

const PATHS = ASSET_PATHS.AVATARS;

const toSource = (path: string): ImageSourcePropType => ({ uri: getAssetUrl(path) });
const toSmallSource = (path: string): ImageSourcePropType => ({ uri: getSmallAssetUrl(path) });

/**
 * Complete-look girl avatars: a single full-body illustration with no mood/home-away
 * variants and no 150×150 small variant. Every mood reuses the one image, and the small
 * registry intentionally uses the full image too (so small renders never 404).
 */
const GIRL_LOOK_PATHS: Record<string, string> = {
  girl4: 'assets/character/char/girl4.webp',
  girl5: 'assets/character/char/girl5.webp',
  girl6: 'assets/character/char/girl6.webp',
};

const buildGirlLookRegistry = (): Record<string, AvatarRegistryEntry> =>
  Object.fromEntries(
    Object.entries(GIRL_LOOK_PATHS).map(([id, path]) => {
      const src = toSource(path);
      return [
        id,
        {
          [AVATAR_MOODS.IDLE]: src,
          [AVATAR_MOODS.HAPPY]: src,
          [AVATAR_MOODS.SAD]: src,
          angry: src,
          home: src,
          away: src,
        },
      ];
    }),
  );

const girlLookRegistry = buildGirlLookRegistry();

type AvatarPathEntry = {
  idle: string;
  happy: string;
  sad: string;
  angry: string;
  home: string;
  away: string;
};

const pathsByKey = PATHS as Record<string, AvatarPathEntry>;

const baseRegistry = (Object.values(AVATAR_IDS) as AvatarId[]).reduce(
  (acc, id) => {
    const pathEntry = pathsByKey[id];
    if (!pathEntry) return acc;
    acc[id] = {
      [AVATAR_MOODS.IDLE]: toSource(pathEntry.idle),
      [AVATAR_MOODS.HAPPY]: toSource(pathEntry.happy),
      [AVATAR_MOODS.SAD]: toSource(pathEntry.sad),
      angry: toSource(pathEntry.angry),
      home: toSource(pathEntry.home),
      away: toSource(pathEntry.away),
    };
    return acc;
  },
  {} as Record<AvatarId, AvatarRegistryEntry>
);

export const AVATAR_REGISTRY: AvatarRegistryType = {
  ...baseRegistry,
  ...girlLookRegistry,
  [MONKEY_AVATAR_ID]: {
    [AVATAR_MOODS.IDLE]: toSource(PATHS.monkey.idle),
    [AVATAR_MOODS.HAPPY]: toSource(PATHS.monkey.happy),
    [AVATAR_MOODS.SAD]: toSource(PATHS.monkey.sad),
    angry: toSource(PATHS.monkey.angry),
    home: toSource(PATHS.monkey.idle),
    away: toSource(PATHS.monkey.idle),
  },
};

/** 150×150 variant registry — use for small avatar renderings (≤ 150 px). */
const baseRegistrySm = (Object.values(AVATAR_IDS) as AvatarId[]).reduce(
  (acc, id) => {
    const pathEntry = pathsByKey[id];
    if (!pathEntry) return acc;
    acc[id] = {
      [AVATAR_MOODS.IDLE]: toSmallSource(pathEntry.idle),
      [AVATAR_MOODS.HAPPY]: toSmallSource(pathEntry.happy),
      [AVATAR_MOODS.SAD]: toSmallSource(pathEntry.sad),
      angry: toSmallSource(pathEntry.angry),
      home: toSmallSource(pathEntry.home),
      away: toSmallSource(pathEntry.away),
    };
    return acc;
  },
  {} as Record<AvatarId, AvatarRegistryEntry>
);

export const AVATAR_REGISTRY_SM: AvatarRegistryType = {
  ...baseRegistrySm,
  // Reuse the full-size illustration (no small variant exists) so small renders never 404.
  ...girlLookRegistry,
  [MONKEY_AVATAR_ID]: {
    [AVATAR_MOODS.IDLE]: toSmallSource(PATHS.monkey.idle),
    [AVATAR_MOODS.HAPPY]: toSmallSource(PATHS.monkey.happy),
    [AVATAR_MOODS.SAD]: toSmallSource(PATHS.monkey.sad),
    angry: toSmallSource(PATHS.monkey.angry),
    home: toSmallSource(PATHS.monkey.idle),
    away: toSmallSource(PATHS.monkey.idle),
  },
};
